import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import { FormField, tcFormValidators, FIELD_TYPES } from '@techchange/components';
import { useLanguageContext, TECHCHANGE_DEFAULT_ASPECT_RATIO_BOOTSTRAP_V4 } from '@techchange/components';
import { messages } from '../../../../i18n';
import { VideoFeed, LibraryVideoProvider } from '@techchange/video';

import { RESOURCE_TYPES } from '../../../../constants';
import ContentEditor from '../../ContentEditor';
import {
	getInitialLanguage,
	useResourceFormData,
	RESOURCE_FORM_FIELDS,
} from '../../hooks/useResourceFormData';
import { RESOURCE_VIDEO_PROVIDERS } from '../../utils/constants';
import { getAspectRatioOptions } from '../../utils/fieldOptions';
import parseVideoSrc from '../../utils/parseVideoSrc';
import { isSafeUrl } from '../../../../utils/urls';
import {
	contentBuilderPropTypes,
	videoDataShape,
	initialValuesShape
} from '../../utils/propTypes';

import LibraryVideoField from './LibraryVideoField';

import './Video.scss';

const propTypes = {

	...contentBuilderPropTypes.contentEditor,

	/**
	 * The Resource type.
	 */
	type: PropTypes.oneOf([RESOURCE_TYPES.VIDEO]).isRequired,

	/**
	 * The initial values of the form.
	 */
	initialValues: PropTypes.shape({

		...initialValuesShape,

		/**
		 * The content data.
		 */
		data: PropTypes.shape(videoDataShape),

	}).isRequired,

};

const DEFAULT_RESOURCE_CONTENT = {
	[RESOURCE_FORM_FIELDS.TITLE]: '',
	[RESOURCE_FORM_FIELDS.DATA]: {
		provider: RESOURCE_VIDEO_PROVIDERS.LIBRARY,
		aspectRatio: TECHCHANGE_DEFAULT_ASPECT_RATIO_BOOTSTRAP_V4,
	},
};

const getInitialFormData = (props) => {

	const { title, data, width, resourceContents, deletedResourceContents } = props.initialValues;

	return {
		[RESOURCE_FORM_FIELDS.TITLE]: title || '',
		[RESOURCE_FORM_FIELDS.DATA]: {
			provider: data?.provider || RESOURCE_VIDEO_PROVIDERS.LIBRARY,
			src: data?.src,
			video_id: data?.video_id,
			aspectRatio: data?.aspectRatio ||
				TECHCHANGE_DEFAULT_ASPECT_RATIO_BOOTSTRAP_V4,
		},
		[RESOURCE_FORM_FIELDS.WIDTH]: width,
		[RESOURCE_FORM_FIELDS.LANGUAGE]: getInitialLanguage(props),
		resource_contents: resourceContents || [{
			[RESOURCE_FORM_FIELDS.LANGUAGE]: props.defaultLanguage,
			...DEFAULT_RESOURCE_CONTENT,
		}],
		deleted_resource_contents: deletedResourceContents || [],
		scope: props.initialValues.scope,
		scope_id: props.initialValues.scope_id,
	};

};

const getInitialInputValidation = () => ({
	[RESOURCE_FORM_FIELDS.TITLE]: [tcFormValidators.required],
	[RESOURCE_FORM_FIELDS.DATA]: {
		provider: [tcFormValidators.required],
		src: [
			tcFormValidators.requiredConditional(
				(formData) => formData.data.provider === RESOURCE_VIDEO_PROVIDERS.URL
			),
			tcFormValidators.url
		],
		video_id: [
			tcFormValidators.requiredConditional(
				(formData) => formData.data.provider === RESOURCE_VIDEO_PROVIDERS.LIBRARY
			)
		],
	},
	resource_contents: [tcFormValidators.arrayContainingObjects(RESOURCE_FORM_FIELDS.LANGUAGE, {
		[RESOURCE_FORM_FIELDS.TITLE]: [tcFormValidators.required],
		[RESOURCE_FORM_FIELDS.DATA]: {
			provider: [tcFormValidators.required],
			src: [
				tcFormValidators.requiredConditional(
					(resourceContent) => resourceContent.data.provider === RESOURCE_VIDEO_PROVIDERS.URL
				),
				tcFormValidators.url
			],
			video_id: [
				tcFormValidators.requiredConditional(
					(resourceContent) => resourceContent.data.provider === RESOURCE_VIDEO_PROVIDERS.LIBRARY
				)
			],
		},
	})],
});

/**
 * Editor for "video" resource types. Admins can makes changes to the
 * "title", "data.provider", "data.src", "data.video_id",
 * and "data.aspectRatio" fields for video resources.
 *
 * When the provider is "Library", the video_id field will be used.
 * When the provider is "URL", the src field will be used.
 *
 * @param {Object} props  The component props.
 *
 * @returns {Object}  The component to render.
 */
const VideoContentEditor = (props) => {

	const {
		id,
		type,
		socket,
		onCancel,
	} = props;

	const intl = useIntl();

	const { preferredLanguage, defaultLanguage } = useLanguageContext();

	const formProps = {
		...props,
		userLanguage: preferredLanguage,
		defaultLanguage,
	};

	const { formData, inputValidation, actions } = useResourceFormData(
		() => getInitialFormData(formProps),
		getInitialInputValidation,
		{ ...formProps, defaultResourceContent: DEFAULT_RESOURCE_CONTENT }
	);

	const ASPECT_RATIO_OPTIONS = getAspectRatioOptions(intl);

	const activeTranslation = formData.resource_contents
		.find((rContent) => rContent.language === formData.language);

	const activeInputValidation = inputValidation?.resource_contents?.data?.[formData.language];

	const isLibraryProvider =
		activeTranslation?.[RESOURCE_FORM_FIELDS.DATA].provider === RESOURCE_VIDEO_PROVIDERS.LIBRARY;
	const isURLProvider =
		activeTranslation?.[RESOURCE_FORM_FIELDS.DATA].provider === RESOURCE_VIDEO_PROVIDERS.URL;

	const formFields = activeTranslation && (
		<>
			<FormField
				type={FIELD_TYPES.TEXT}
				name={RESOURCE_FORM_FIELDS.TITLE}
				value={activeTranslation[RESOURCE_FORM_FIELDS.TITLE]}
				label={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_VIDEO_FIELD_LABEL_TEXT_TITLE)}
				placeholder={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_VIDEO_FIELD_PLACEHOLDER_TEXT_TITLE)}
				isInvalid={activeInputValidation?.[RESOURCE_FORM_FIELDS.TITLE]?.invalid}
				error={activeInputValidation?.[RESOURCE_FORM_FIELDS.TITLE]?.error}
				isEditing
				isRequired
				onChange={actions.updateField} />
			<FormField
				type={FIELD_TYPES.RADIO}
				className="video-provider"
				name="provider"
				label={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_VIDEO_FIELD_LABEL_PROVIDER)}
				help={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_VIDEO_FIELD_HELP_PROVIDER)}
				value={activeTranslation?.[RESOURCE_FORM_FIELDS.DATA]?.provider}
				isInvalid={activeInputValidation?.[RESOURCE_FORM_FIELDS.DATA]?.provider?.invalid}
				error={activeInputValidation?.[RESOURCE_FORM_FIELDS.DATA]?.provider?.error}
				options={[
					{
						id: 'resource_video_provider_library',
						value: RESOURCE_VIDEO_PROVIDERS.LIBRARY,
						label: intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_VIDEO_FIELD_PROVIDER_OPTIONS_LABEL_LIBRARY),
					},
					{
						id: 'resource_video_provider_url',
						value: RESOURCE_VIDEO_PROVIDERS.URL,
						label: intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_VIDEO_FIELD_PROVIDER_OPTIONS_LABEL_URL),
					},
				]}
				isEditing
				onChange={actions.updateField} />
			{isLibraryProvider &&
				<LibraryVideoProvider
					videoId={activeTranslation?.data.video_id}
					scope={formData?.scope}
					scopeId={formData?.scope_id}
					socket={socket}>
					<LibraryVideoField
						formData={formData}
						videoInputValidation={activeInputValidation?.[RESOURCE_FORM_FIELDS.DATA]?.video_id}
						onChange={actions.updateField} />
				</LibraryVideoProvider>
			}
			{isURLProvider &&
				<>
					<FormField
						type={FIELD_TYPES.TEXT}
						className="mb-4"
						name="src"
						value={activeTranslation?.[RESOURCE_FORM_FIELDS.DATA]?.src}
						label={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_VIDEO_FIELD_LABEL_TEXT_SRC)}
						placeholder={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_VIDEO_FIELD_PLACEHOLDER_TEXT_SRC)}
						isInvalid={activeInputValidation?.[RESOURCE_FORM_FIELDS.DATA]?.src?.invalid}
						error={activeInputValidation?.[RESOURCE_FORM_FIELDS.DATA]?.src?.error}
						isLabelHidden
						isEditing
						onChange={(name, value) => actions.updateField(name, parseVideoSrc(value))} />
					{isSafeUrl(activeTranslation?.[RESOURCE_FORM_FIELDS.DATA]?.src) &&
						<div className="mt-3">
							<VideoFeed
								id={id}
								src={activeTranslation?.[RESOURCE_FORM_FIELDS.DATA]?.src}
								aspectRatio={activeTranslation?.[RESOURCE_FORM_FIELDS.DATA]?.aspectRatio} />
						</div>
					}
				</>
			}
			<FormField
				id={`aspectRatio-select-field-${id}`}
				type={FIELD_TYPES.SELECT}
				name="aspectRatio"
				value={activeTranslation?.[RESOURCE_FORM_FIELDS.DATA]?.aspectRatio}
				options={ASPECT_RATIO_OPTIONS}
				label={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_VIDEO_FIELD_LABEL_TEXT_ASPECT_RATIO)}
				help={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_VIDEO_FIELD_HELP_TEXT_ASPECT_RATIO)}
				isEditing
				onChange={actions.updateField} />
		</>
	);

	return (
		<ContentEditor
			id={id}
			type={type}
			formData={formData}
			inputValidation={inputValidation}
			actions={actions}
			formFields={formFields}
			onCancel={onCancel} />
	);

};

VideoContentEditor.propTypes = propTypes;

export default VideoContentEditor;
